import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../config/firebaseConfig';
import { collection, getDocs, addDoc, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { QRCodeCanvas } from 'qrcode.react';
import { Modal, Button, Form, Spinner, Alert, InputGroup } from 'react-bootstrap';
import ScrollDownArrow from '../components/shared/ScrollDownArrow'; 

const ManageBookPages = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', videoUrl: '', orderUrl: '', flipbookEmbedUrl: '' });
  
  // Basitleştirilmiş Dosya Yükleme (Sadece Kapak Resmi, Ses ve 3 Bonus PDF)
  const [files, setFiles] = useState({ cover: null, audio: null, bonus1: null, bonus2: null, bonus3: null });

  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [editFiles, setEditFiles] = useState({ cover: null, audio: null, bonus1: null, bonus2: null, bonus3: null });

  const [copiedId, setCopiedId] = useState(null);
  const [alertMsg, setAlertMsg] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; 

  const showAlert = (type, text) => {
    setAlertMsg({ type, text });
    setTimeout(() => setAlertMsg({ type: '', text: '' }), 4000);
  };

  const fetchBooks = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'bookPages'));
      setBooks(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      showAlert('danger', 'Failed to load book pages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBooks(); }, [fetchBooks]);

  const uploadFileToServer = async (file) => {
    if(!file) return '';
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      const response = await fetch('https://studio.littlehibba.com/upload.php', {
        method: 'POST',
        body: uploadData,
      });
      const data = await response.json();
      if (data.success) return data.url;
      throw new Error(data.message);
    } catch (error) {
      alert(`Error uploading ${file.name}: ` + error.message);
      return null;
    }
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      let coverUrl = await uploadFileToServer(files.cover);
      let audioUrl = await uploadFileToServer(files.audio);
      let bonus1Url = await uploadFileToServer(files.bonus1);
      let bonus2Url = await uploadFileToServer(files.bonus2);
      let bonus3Url = await uploadFileToServer(files.bonus3);

      const finalBookData = {
        title: formData.title,
        description: formData.description,
        videoUrl: formData.videoUrl || '',
        orderUrl: formData.orderUrl || '',
        flipbookEmbedUrl: formData.flipbookEmbedUrl || '', // Dışarıdan alınan Iframe Kaynak Linki
        coverUrl: coverUrl || '',
        audioUrl: audioUrl || '',
        bonus1Url: bonus1Url || '',
        bonus2Url: bonus2Url || '',
        bonus3Url: bonus3Url || ''
      };

      await addDoc(collection(db, 'bookPages'), finalBookData);
      showAlert('success', 'Landing page created successfully!');
      
      setFormData({ title: '', description: '', videoUrl: '', orderUrl: '', flipbookEmbedUrl: '' });
      setFiles({ cover: null, audio: null, bonus1: null, bonus2: null, bonus3: null });
      document.getElementById('bookPageForm').reset();
      fetchBooks();
    } catch (err) {
      showAlert('danger', 'An error occurred while creating the page.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = (book) => {
    setEditingBook(book);
    setEditFiles({ cover: null, audio: null, bonus1: null, bonus2: null, bonus3: null });
    setShowEditModal(true);
  };

  const handleUpdatePage = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      let updatedData = {
        title: editingBook.title,
        description: editingBook.description,
        videoUrl: editingBook.videoUrl || '',
        orderUrl: editingBook.orderUrl || '',
        flipbookEmbedUrl: editingBook.flipbookEmbedUrl || ''
      };

      if (editFiles.cover) updatedData.coverUrl = await uploadFileToServer(editFiles.cover);
      if (editFiles.audio) updatedData.audioUrl = await uploadFileToServer(editFiles.audio);
      if (editFiles.bonus1) updatedData.bonus1Url = await uploadFileToServer(editFiles.bonus1);
      if (editFiles.bonus2) updatedData.bonus2Url = await uploadFileToServer(editFiles.bonus2);
      if (editFiles.bonus3) updatedData.bonus3Url = await uploadFileToServer(editFiles.bonus3);

      await updateDoc(doc(db, 'bookPages', editingBook.id), updatedData);

      showAlert('success', 'Page updated successfully!');
      setShowEditModal(false);
      fetchBooks();
    } catch (error) {
      showAlert('danger', 'Failed to update the page.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Are you sure you want to delete this landing page?")) {
      await deleteDoc(doc(db, 'bookPages', id));
      showAlert('warning', 'Landing page deleted.');
      fetchBooks();
      if (currentBooks.length === 1 && currentPage > 1) setCurrentPage(currentPage - 1);
    }
  };

  const downloadQR = (id, title) => {
    const canvas = document.getElementById(`qr-${id}`);
    const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
    let downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `${title.replace(/\s+/g, '-').toLowerCase()}-QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const copyToClipboard = (id, url) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000); 
  };

  const filteredBooks = books.filter(book => 
    book.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (book.description && book.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBooks.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <div className="min-vh-100 d-flex justify-content-center align-items-center"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div className="container mt-4 mb-5 pb-5 position-relative">
      <ScrollDownArrow />
      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
        <div><h2 className="fw-bold text-dark mb-0">Book Landing Pages</h2><p className="text-muted mb-0 mt-1">Generate and manage digital extensions for your physical books.</p></div>
      </div>
      {alertMsg.text && <Alert variant={alertMsg.type} className="shadow-sm">{alertMsg.text}</Alert>}
      
      <div className="card border-0 shadow-sm rounded-4 mb-5">
        <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4"><h5 className="fw-bold text-primary"><i className="bi bi-plus-circle me-2"></i>Create New Page</h5></div>
        <div className="card-body p-4">
          <Form id="bookPageForm" onSubmit={handleCreatePage}>
            <div className="row g-4">
              <div className="col-lg-6">
                <div className="bg-light p-3 rounded-3 border h-100">
                  <h6 className="fw-bold mb-3 text-secondary border-bottom pb-2">Page Details & Links</h6>
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Book Title <span className="text-danger">*</span></Form.Label><Form.Control type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></Form.Group>
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Description</Form.Label><Form.Control as="textarea" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></Form.Group>
                  
                  {/* YENİ: SADECE LİNK GİRİLECEK (Örn: https://www.flipbookpdf.net/web/site/...) */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-primary">Flipbook Embed Link (SRC URL)</Form.Label>
                    <Form.Control type="url" placeholder="https://www.flipbookpdf.net/web/site/..." value={formData.flipbookEmbedUrl} onChange={(e) => setFormData({ ...formData, flipbookEmbedUrl: e.target.value })} />
                    <small className="text-muted d-block mt-1" style={{fontSize: '0.75rem'}}>Sadece iframe içindeki <b>src="..."</b> linkini yapıştırın.</small>
                  </Form.Group>
                  
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Order Book URL (Shop Link)</Form.Label><Form.Control type="url" placeholder="https://..." value={formData.orderUrl} onChange={(e) => setFormData({ ...formData, orderUrl: e.target.value })} /></Form.Group>
                  <Form.Group><Form.Label className="fw-bold small">YouTube Video URL</Form.Label><Form.Control type="url" placeholder="https://youtube.com/watch?v=..." value={formData.videoUrl} onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })} /></Form.Group>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="bg-light p-3 rounded-3 border h-100">
                  <h6 className="fw-bold mb-3 text-secondary border-bottom pb-2">Media Files</h6>
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Cover Image (Fallback)</Form.Label><Form.Control type="file" size="sm" accept="image/*" onChange={(e) => setFiles({ ...files, cover: e.target.files[0] })} /><small className="text-muted d-block mt-1" style={{fontSize: '0.7rem'}}>Flipbook yoksa gösterilecek kapak resmi.</small></Form.Group>
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Audio Story (MP3)</Form.Label><Form.Control type="file" size="sm" accept="audio/mpeg" onChange={(e) => setFiles({ ...files, audio: e.target.files[0] })} /></Form.Group>
                  <div className="p-2 border rounded bg-white mt-3">
                    <Form.Label className="fw-bold small mb-2 d-block">Bonus Materials (Up to 3 Columns)</Form.Label>
                    <Form.Control type="file" size="sm" accept="application/pdf" className="mb-2" onChange={(e) => setFiles({ ...files, bonus1: e.target.files[0] })} />
                    <Form.Control type="file" size="sm" accept="application/pdf" className="mb-2" onChange={(e) => setFiles({ ...files, bonus2: e.target.files[0] })} />
                    <Form.Control type="file" size="sm" accept="application/pdf" onChange={(e) => setFiles({ ...files, bonus3: e.target.files[0] })} />
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-4"><Button type="submit" variant="primary" className="px-5 py-2 fw-bold shadow-sm" disabled={isUploading}>{isUploading ? <><Spinner as="span" animation="border" size="sm" className="me-2"/> Uploading...</> : 'Generate Landing Page & QR'}</Button></div>
          </Form>
        </div>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end border-bottom pb-3 mb-4 gap-3">
        <h4 className="fw-bold mb-0">Active Pages & QR Codes</h4>
        <div style={{ maxWidth: '300px', width: '100%' }}><InputGroup className="shadow-sm"><InputGroup.Text className="bg-white text-muted border-end-0"><i className="bi bi-search"></i></InputGroup.Text><Form.Control type="text" placeholder="Search books..." className="border-start-0 ps-0 bg-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></InputGroup></div>
      </div>
      
      {books.length === 0 ? (
        <div className="text-center p-5 bg-light rounded-4 border border-dashed"><i className="bi bi-folder2-open text-muted" style={{fontSize: '3rem'}}></i><h5 className="mt-3 text-muted">No pages created yet.</h5></div>
      ) : filteredBooks.length === 0 ? (
        <div className="text-center p-5 bg-light rounded-4 border border-dashed"><i className="bi bi-search text-muted" style={{fontSize: '3rem'}}></i><h5 className="mt-3 text-muted">No results found.</h5></div>
      ) : (
        <>
          <div className="row g-4">
            {currentBooks.map((book) => {
              const landingPageUrl = `https://studio.littlehibba.com/book/${book.id}`; 
              const isCopied = copiedId === book.id;
              return (
                <div className="col-lg-4 col-md-6" key={book.id}>
                  <div className="card h-100 border-0 shadow-sm hover-shadow transition-all rounded-4 overflow-hidden">
                    <div className="card-header bg-white text-primary text-center py-3 border-bottom"><h6 className="mb-0 text-truncate px-2 fw-bold" title={book.title}>{book.title}</h6></div>
                    <div className="card-body text-center p-4 d-flex flex-column">
                      <div className="bg-light p-3 rounded-4 d-inline-block mx-auto mb-4 border"><QRCodeCanvas id={`qr-${book.id}`} value={landingPageUrl} size={160} level={"H"} includeMargin={false} /></div>
                      <div className="mt-auto">
                        <label className="small text-muted fw-bold mb-1 text-start w-100">Live Link</label>
                        <div className="input-group input-group-sm mb-4 shadow-sm">
                          <input type="text" className="form-control bg-light text-muted" value={landingPageUrl} readOnly />
                          <button className={`btn ${isCopied ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => copyToClipboard(book.id, landingPageUrl)} title="Copy Link">{isCopied ? <i className="bi bi-check2-all"></i> : <i className="bi bi-clipboard"></i>}</button>
                        </div>
                        <div className="d-flex justify-content-between gap-2">
                          <button className="btn btn-primary btn-sm flex-grow-1 fw-medium" onClick={() => downloadQR(book.id, book.title)}><i className="bi bi-download me-1"></i> QR</button>
                          <button className="btn btn-light border btn-sm flex-grow-1 fw-medium" onClick={() => handleEditClick(book)}><i className="bi bi-pencil me-1"></i> Edit</button>
                          <button className="btn btn-outline-danger btn-sm px-3" onClick={() => handleDelete(book.id)} title="Delete Page"><i className="bi bi-trash3"></i></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-5"><nav><ul className="pagination shadow-sm rounded-pill overflow-hidden"><li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}><button className="page-link border-0 text-dark" onClick={() => paginate(currentPage - 1)}>&laquo; Prev</button></li>{[...Array(totalPages)].map((_, i) => (<li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}><button className="page-link border-0" onClick={() => paginate(i + 1)}>{i + 1}</button></li>))}<li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}><button className="page-link border-0 text-dark" onClick={() => paginate(currentPage + 1)}>Next &raquo;</button></li></ul></nav></div>
          )}
        </>
      )}

      {/* EDIT MODAL */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light"><Modal.Title className="fw-bold"><i className="bi bi-pencil-square me-2 text-primary"></i>Edit Book Page</Modal.Title></Modal.Header>
        <Modal.Body className="p-4">
          {editingBook && (
            <Form onSubmit={handleUpdatePage}>
              <div className="row g-4">
                <div className="col-md-6">
                  <Form.Group><Form.Label className="fw-bold small">Book Title</Form.Label><Form.Control type="text" value={editingBook.title} onChange={(e) => setEditingBook({...editingBook, title: e.target.value})} required /></Form.Group>
                  <Form.Group className="mt-3"><Form.Label className="fw-bold small">Description</Form.Label><Form.Control as="textarea" rows={3} value={editingBook.description} onChange={(e) => setEditingBook({...editingBook, description: e.target.value})} /></Form.Group>
                  <Form.Group className="mt-3"><Form.Label className="fw-bold small text-primary">Flipbook Embed Link</Form.Label><Form.Control type="url" value={editingBook.flipbookEmbedUrl || ''} onChange={(e) => setEditingBook({...editingBook, flipbookEmbedUrl: e.target.value})} /></Form.Group>
                  <Form.Group className="mt-3"><Form.Label className="fw-bold small">Order Book URL</Form.Label><Form.Control type="url" value={editingBook.orderUrl || ''} onChange={(e) => setEditingBook({...editingBook, orderUrl: e.target.value})} /></Form.Group>
                  <Form.Group className="mt-3"><Form.Label className="fw-bold small">YouTube Video URL</Form.Label><Form.Control type="url" value={editingBook.videoUrl || ''} onChange={(e) => setEditingBook({...editingBook, videoUrl: e.target.value})} /></Form.Group>
                </div>
                <div className="col-md-6 border-start px-4">
                  <div className="alert alert-info py-2 small border-0"><i className="bi bi-info-circle me-2"></i> Only upload new files to replace existing ones.</div>
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Replace Cover (Fallback)</Form.Label><Form.Control type="file" size="sm" accept="image/*" onChange={(e) => setEditFiles({...editFiles, cover: e.target.files[0]})} />{editingBook.coverUrl && <span className="d-block mt-1 small text-success"><i className="bi bi-check-circle me-1"></i>Active</span>}</Form.Group>
                  <Form.Group className="mb-3"><Form.Label className="fw-bold small">Replace Audio (MP3)</Form.Label><Form.Control type="file" size="sm" accept="audio/mpeg" onChange={(e) => setEditFiles({...editFiles, audio: e.target.files[0]})} />{editingBook.audioUrl && <span className="d-block mt-1 small text-success"><i className="bi bi-check-circle me-1"></i>Active</span>}</Form.Group>
                  <Form.Group className="mb-2"><Form.Label className="fw-bold small">Replace Bonus 1 (PDF)</Form.Label><Form.Control type="file" size="sm" accept="application/pdf" onChange={(e) => setEditFiles({...editFiles, bonus1: e.target.files[0]})} /></Form.Group>
                  <Form.Group className="mb-2"><Form.Label className="fw-bold small">Replace Bonus 2 (PDF)</Form.Label><Form.Control type="file" size="sm" accept="application/pdf" onChange={(e) => setEditFiles({...editFiles, bonus2: e.target.files[0]})} /></Form.Group>
                  <Form.Group className="mb-2"><Form.Label className="fw-bold small">Replace Bonus 3 (PDF)</Form.Label><Form.Control type="file" size="sm" accept="application/pdf" onChange={(e) => setEditFiles({...editFiles, bonus3: e.target.files[0]})} /></Form.Group>
                </div>
              </div>
              <div className="d-flex justify-content-end mt-4 pt-3 border-top"><Button variant="light" className="me-2 border fw-medium" onClick={() => setShowEditModal(false)}>Cancel</Button><Button variant="primary" type="submit" disabled={isUpdating} className="fw-bold px-4">{isUpdating ? <><Spinner as="span" animation="border" size="sm" className="me-2"/> Updating...</> : 'Save Changes'}</Button></div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ManageBookPages;